import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, TrendingUp, TrendingDown, Calendar, Target, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLivePrices } from '@/hooks/useLivePrices';

interface Recommendation {
  id: string;
  analysis_date: string;
  top_coin_recommendation: string;
  top_coin_reasoning: string;
  top_roi_token: string;
  top_roi_reasoning: string;
  raw_analysis_data: any;
}

const PerformanceTracker = () => {
  const [timeframe, setTimeframe] = useState<'7d' | '30d' | 'all'>('30d');
  const { prices: livePrices } = useLivePrices();

  const { data: recommendations, isLoading } = useQuery({
    queryKey: ['performance-tracker', timeframe],
    queryFn: async () => {
      const daysAgo = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : 365;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysAgo);

      const { data, error } = await supabase
        .from('ai_analysis')
        .select('*')
        .gte('analysis_date', startDate.toISOString().split('T')[0])
        .order('analysis_date', { ascending: false });

      if (error) throw error;
      return data as Recommendation[];
    },
  });

  // Fetch snapshot entry prices for all recommendation dates/symbols
  const { data: snapshots } = useQuery({
    queryKey: ['performance-snapshots', recommendations],
    queryFn: async () => {
      if (!recommendations || recommendations.length === 0) return new Map<string, number>();

      const symbolDatePairs = recommendations.flatMap(r => [
        { symbol: r.top_coin_recommendation, date: r.analysis_date },
        { symbol: r.top_roi_token, date: r.analysis_date },
      ]);

      const symbols = [...new Set(symbolDatePairs.map(p => p.symbol))];
      const dates = [...new Set(symbolDatePairs.map(p => p.date))];

      const { data, error } = await supabase
        .from('daily_snapshots')
        .select('token_symbol, snapshot_date, price')
        .in('token_symbol', symbols)
        .in('snapshot_date', dates);

      if (error) throw error;

      const map = new Map<string, number>();
      (data || []).forEach(row => {
        map.set(`${row.token_symbol}::${row.snapshot_date}`, row.price);
      });
      return map;
    },
    enabled: !!recommendations && recommendations.length > 0,
  });

  const calculateROI = (symbol: string, recommendedDate: string) => {
    const liveData = livePrices.get(symbol.toUpperCase());
    if (!liveData) return null;

    const entryPrice = snapshots?.get(`${symbol}::${recommendedDate}`);
    if (!entryPrice || entryPrice === 0) return null;

    const roi = ((liveData.price - entryPrice) / entryPrice) * 100;

    return {
      currentPrice: liveData.price,
      entryPrice,
      roi,
      change24h: liveData.change24h,
      change7d: liveData.change7d ?? 0,
    };
  };

  const getPerformanceBadge = (roi: number | null) => {
    if (roi === null) return <Badge variant="secondary">No Snapshot Data</Badge>;
    if (roi > 50) return <Badge className="bg-green-600 text-white">Exceptional +{roi.toFixed(1)}%</Badge>;
    if (roi > 20) return <Badge className="bg-green-500 text-white">Strong +{roi.toFixed(1)}%</Badge>;
    if (roi > 5) return <Badge className="bg-green-400 text-white">Positive +{roi.toFixed(1)}%</Badge>;
    if (roi > -5) return <Badge variant="secondary">Neutral {roi.toFixed(1)}%</Badge>;
    if (roi > -20) return <Badge variant="destructive">Down {roi.toFixed(1)}%</Badge>;
    return <Badge className="bg-red-700 text-white">Significant Loss {roi.toFixed(1)}%</Badge>;
  };

  const formatPrice = (price: number) =>
    price < 1 ? `$${price.toFixed(4)}` : `$${price.toFixed(2)}`;

  const RecommendationCard = ({ symbol, reasoning, date, type }: { symbol: string; reasoning: string; date: string; type: 'coin' | 'roi' }) => {
    const performance = calculateROI(symbol, date);
    const daysAgo = Math.floor((Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));

    return (
      <Card className="overflow-hidden hover:shadow-lg transition-shadow">
        <CardHeader className="bg-muted/50">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <CardTitle className="text-2xl">{symbol}</CardTitle>
                {type === 'roi' && <Badge variant="outline" className="text-xs">High ROI Pick</Badge>}
              </div>
              <CardDescription className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4" />
                Recommended {daysAgo} days ago ({new Date(date).toLocaleDateString()})
              </CardDescription>
            </div>
            <div className="text-right">
              {getPerformanceBadge(performance?.roi ?? null)}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {performance ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Entry Price</p>
                  <p className="text-sm font-semibold">{formatPrice(performance.entryPrice)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Current Price</p>
                  <p className="text-sm font-semibold">{formatPrice(performance.currentPrice)}</p>
                  <Badge className="bg-success/20 text-success border-success/30 text-[10px] mt-1" variant="outline">Live</Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">24h Change</p>
                  <p className={`text-sm font-semibold flex items-center gap-1 ${performance.change24h > 0 ? 'text-success' : 'text-destructive'}`}>
                    {performance.change24h > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {performance.change24h.toFixed(2)}%
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">P&L Since Pick</p>
                  <p className={`text-sm font-semibold flex items-center gap-1 ${performance.roi > 0 ? 'text-success' : 'text-destructive'}`}>
                    {performance.roi > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {performance.roi > 0 ? '+' : ''}{performance.roi.toFixed(2)}%
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-muted/30 rounded-lg text-sm text-muted-foreground">
                No snapshot data available for this recommendation. P&L requires daily price snapshots.
              </div>
            )}

            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Why We Recommended {symbol}
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {reasoning.split('\n\n')[0]}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const calculateStats = () => {
    if (!recommendations) return null;

    const allRecs = recommendations.flatMap(r => [
      { symbol: r.top_coin_recommendation, date: r.analysis_date, type: 'coin' as const },
      { symbol: r.top_roi_token, date: r.analysis_date, type: 'roi' as const },
    ]);

    const withPerf = allRecs
      .map(rec => ({ ...rec, performance: calculateROI(rec.symbol, rec.date) }))
      .filter(rec => rec.performance !== null);

    if (withPerf.length === 0) return null;

    const winners = withPerf.filter(r => r.performance!.roi > 0).length;
    const avgROI = withPerf.reduce((s, r) => s + r.performance!.roi, 0) / withPerf.length;
    const best = withPerf.reduce((b, c) => c.performance!.roi > (b.performance?.roi ?? -Infinity) ? c : b);

    return { totalRecommendations: withPerf.length, winners, winRate: (winners / withPerf.length) * 100, avgROI, bestPerformer: best };
  };

  const stats = calculateStats();

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-4 py-6">
          <Link to="/"><Button variant="ghost" size="sm" className="gap-2 mb-4"><ArrowLeft className="w-4 h-4" />Back to Home</Button></Link>
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3"><Activity className="w-8 h-8 text-primary" />AI Performance Tracker</h1>
          <p className="text-muted-foreground">Track the real-world performance of our AI-powered crypto recommendations using live prices and daily snapshots</p>
        </div>
      </div>

      {stats && (
        <div className="border-b bg-gradient-to-br from-primary/5 to-primary/10">
          <div className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card><CardHeader className="pb-3"><CardDescription>Total Recommendations</CardDescription><CardTitle className="text-3xl">{stats.totalRecommendations}</CardTitle></CardHeader></Card>
              <Card><CardHeader className="pb-3"><CardDescription>Win Rate</CardDescription><CardTitle className="text-3xl text-success">{stats.winRate.toFixed(1)}%</CardTitle></CardHeader></Card>
              <Card><CardHeader className="pb-3"><CardDescription>Average ROI</CardDescription><CardTitle className={`text-3xl ${stats.avgROI > 0 ? 'text-success' : 'text-destructive'}`}>{stats.avgROI > 0 ? '+' : ''}{stats.avgROI.toFixed(1)}%</CardTitle></CardHeader></Card>
              <Card><CardHeader className="pb-3"><CardDescription>Best Performer</CardDescription><CardTitle className="text-2xl text-success flex items-center gap-2">{stats.bestPerformer.symbol}<span className="text-lg">+{stats.bestPerformer.performance?.roi.toFixed(1)}%</span></CardTitle></CardHeader></Card>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-12">
        <div className="flex justify-center mb-8">
          <Tabs value={timeframe} onValueChange={(v) => setTimeframe(v as any)} className="w-full max-w-md">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="7d">Last 7 Days</TabsTrigger>
              <TabsTrigger value="30d">Last 30 Days</TabsTrigger>
              <TabsTrigger value="all">All Time</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <Card className="mb-8 border-warning/20 bg-warning/5">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              <strong>Performance Note:</strong> P&L is calculated using actual entry-day snapshots and live prices.
              "No Snapshot Data" means the daily snapshot wasn't captured for that date. This is not financial advice.
            </p>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="animate-pulse space-y-4"><div className="h-48 bg-muted rounded-lg" /><div className="h-48 bg-muted rounded-lg" /></div>
        ) : recommendations && recommendations.length > 0 ? (
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="all">All Recommendations</TabsTrigger>
              <TabsTrigger value="coins">Best Coins</TabsTrigger>
              <TabsTrigger value="roi">High ROI Tokens</TabsTrigger>
            </TabsList>
            <TabsContent value="all" className="space-y-6">
              {recommendations.flatMap(rec => [
                <RecommendationCard key={`${rec.id}-coin`} symbol={rec.top_coin_recommendation} reasoning={rec.top_coin_reasoning} date={rec.analysis_date} type="coin" />,
                <RecommendationCard key={`${rec.id}-roi`} symbol={rec.top_roi_token} reasoning={rec.top_roi_reasoning} date={rec.analysis_date} type="roi" />,
              ])}
            </TabsContent>
            <TabsContent value="coins" className="space-y-6">
              {recommendations.map(rec => <RecommendationCard key={`${rec.id}-coin`} symbol={rec.top_coin_recommendation} reasoning={rec.top_coin_reasoning} date={rec.analysis_date} type="coin" />)}
            </TabsContent>
            <TabsContent value="roi" className="space-y-6">
              {recommendations.map(rec => <RecommendationCard key={`${rec.id}-roi`} symbol={rec.top_roi_token} reasoning={rec.top_roi_reasoning} date={rec.analysis_date} type="roi" />)}
            </TabsContent>
          </Tabs>
        ) : (
          <Card><CardContent className="text-center py-12"><p className="text-muted-foreground">No recommendations found for this timeframe.</p></CardContent></Card>
        )}
      </div>
    </div>
  );
};

export default PerformanceTracker;
